import { array, nonEmptyArray, option, record, string } from 'fp-ts';
import { constant, flow, pipe } from 'fp-ts/function';
import { WithEnterpriseId } from '@mews-commander/types';
import { values } from '@mews/fp';

type Entity = { externalIdentifier?: string | null } & WithEnterpriseId;
type GroupedEntities<T> = Record<string, nonEmptyArray.NonEmptyArray<T>>;

const groupEntitiesByEnterpriseId = <T extends Entity>(entities: Array<T>) =>
    pipe(
        entities,
        option.fromPredicate(array.isNonEmpty),
        option.map(nonEmptyArray.groupBy((entity) => entity.enterpriseId)),
        option.getOrElse(constant<GroupedEntities<T>>({}))
    );

const getEntitiesExternalIdentifiersGroups = <T extends Entity>(groupedEntities: GroupedEntities<T>) =>
    pipe(
        groupedEntities,
        record.map(array.filterMap((entity) => option.fromNullable(entity.externalIdentifier))),
        values
    );

export const getExternalIdentifiersGroups = flow(groupEntitiesByEnterpriseId, getEntitiesExternalIdentifiersGroups);

export const getEntitiesExternalIdentifiersIntersection = (externalIdentifiersGroups: string[][]) =>
    pipe(
        externalIdentifiersGroups,
        option.fromPredicate(array.isNonEmpty),
        option.map(([head, ...tail]) =>
            array.isEmpty(tail) ? head : pipe(tail, array.reduce(head, array.intersection(string.Eq)))
        ),
        option.map(array.uniq(string.Eq)),
        option.getOrElse(constant<string[]>([])),
        array.sort(string.Ord)
    );
